"use client";

import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

const STORAGE_PREFIX = "scroll-restore:";
const SAVE_DEBOUNCE_MS = 150;
const RESTORE_POLL_INTERVAL_MS = 60;
const RESTORE_MAX_WAIT_MS = 3000;
const GUARD_WINDOW_MS = 800;
const GUARD_CHECK_INTERVAL_MS = 100;
const SCROLL_TOP_THRESHOLD = 10;

// Module-level storage: survives SPA navigations, immune to
// history.replaceState({}) wiping history.state. Resets only on full
// page reload which is the desired behaviour (start fresh on reload).
const memoryPositions = new Map<string, number>();

/**
 * Persists and restores scroll position so that back-navigation can
 * resume exactly where the user left off.
 *
 * Restoration triggers automatically when the scroll container is at
 * the top (scrollTop ≤ threshold) and a saved position exists. This
 * handles both fresh mounts and cached component trees (Next.js Router
 * Cache) without any one-shot flags that can get stuck.
 *
 * Storage layers (read in priority order):
 *   1. Module-level Map — survives SPA navigations, immune to
 *      history.state being overwritten by other replaceState calls.
 *   2. history.state — scoped to the exact history entry.
 *   3. sessionStorage — shared across entries, last resort.
 *
 * Writes go to all three layers simultaneously.
 */
export function useScrollRestore(
  key: string,
  scrollRef: React.RefObject<HTMLElement | null>,
  options?: { debounceMs?: number; restore?: boolean },
) {
  const debounceMs = options?.debounceMs ?? SAVE_DEBOUNCE_MS;
  const shouldRestore = options?.restore ?? true;
  const stateKey = `${STORAGE_PREFIX}${key}`;
  const storageKey = stateKey;

  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingScrollTop = useRef<number | null>(null);

  const restoringRef = useRef(false);
  const bfcacheRef = useRef(false);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const guardTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);

  function readPosition(): number | null {
    const mem = memoryPositions.get(stateKey);
    if (typeof mem === "number" && mem > 0) return mem;
    try {
      const hs = window.history.state;
      if (hs && typeof hs === "object" && typeof hs[stateKey] === "number") {
        return hs[stateKey];
      }
    } catch { /* ignore */ }
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw) {
        const n = Number.parseInt(raw, 10);
        if (Number.isFinite(n) && n > 0) return n;
      }
    } catch { /* ignore */ }
    return null;
  }

  function writePosition(scrollTop: number) {
    const rounded = Math.round(scrollTop);
    memoryPositions.set(stateKey, rounded);
    try {
      const hs = window.history.state ?? {};
      window.history.replaceState({ ...hs, [stateKey]: rounded }, "");
    } catch { /* ignore */ }
    try {
      sessionStorage.setItem(storageKey, String(rounded));
    } catch { /* ignore */ }
  }

  function flushSave() {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    if (pendingScrollTop.current !== null) {
      writePosition(pendingScrollTop.current);
      pendingScrollTop.current = null;
    }
  }

  useEffect(() => {
    function handlePageShow(e: PageTransitionEvent) {
      if (e.persisted) {
        bfcacheRef.current = true;
      }
    }
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  useEffect(() => {
    function handleVisibilityFlush() {
      if (document.visibilityState === "hidden") flushSave();
    }
    document.addEventListener("visibilitychange", handleVisibilityFlush);
    window.addEventListener("pagehide", flushSave);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityFlush);
      window.removeEventListener("pagehide", flushSave);
      flushSave();
    };
  }, [storageKey]);

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      restoringRef.current = false;
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      if (guardTimerRef.current) {
        clearTimeout(guardTimerRef.current);
        guardTimerRef.current = null;
      }
    };
  }, []);

  // Restore: runs after DOM mutations but before paint. Uses scrollTop
  // position as the trigger — when the container is at the top and a
  // saved position exists, restoration kicks in. No one-shot flag needed.
  useLayoutEffect(() => {
    if (!shouldRestore) {
      memoryPositions.delete(stateKey);
      return;
    }
    if (bfcacheRef.current) return;
    const el = scrollRef.current;
    if (!el) return;

    // Already restoring (guard window active) — let it finish.
    if (restoringRef.current) return;

    // Container is scrolled past the threshold — the user (or a previous
    // restore) has already positioned it. Nothing to do.
    if (el.scrollTop > SCROLL_TOP_THRESHOLD) return;

    const savedPos = readPosition();
    if (savedPos === null || savedPos <= SCROLL_TOP_THRESHOLD) return;
    const scrollTop: number = savedPos;

    restoringRef.current = true;
    cancelledRef.current = false;
    const start = Date.now();

    function applyAndGuard() {
      if (!el) return;
      el.scrollTop = scrollTop;

      const guardStart = Date.now();

      function guardCheck() {
        if (cancelledRef.current || !el) return;
        if (Date.now() - guardStart > GUARD_WINDOW_MS) {
          restoringRef.current = false;
          return;
        }
        if (
          el.scrollTop < scrollTop * 0.5 &&
          el.scrollHeight >= scrollTop + el.clientHeight
        ) {
          el.scrollTop = scrollTop;
        }
        guardTimerRef.current = setTimeout(guardCheck, GUARD_CHECK_INTERVAL_MS);
      }

      guardTimerRef.current = setTimeout(guardCheck, GUARD_CHECK_INTERVAL_MS);
    }

    function tryRestore() {
      if (cancelledRef.current || !el) return;

      if (el.scrollHeight >= scrollTop + el.clientHeight) {
        applyAndGuard();
        return;
      }

      if (Date.now() - start < RESTORE_MAX_WAIT_MS) {
        pollTimerRef.current = setTimeout(tryRestore, RESTORE_POLL_INTERVAL_MS);
        return;
      }

      applyAndGuard();
    }

    tryRestore();
  });

  const onScroll = useCallback(
    (e: React.UIEvent<HTMLElement>) => {
      if (restoringRef.current) return;

      const scrollTop = e.currentTarget.scrollTop;
      pendingScrollTop.current = scrollTop;

      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

      saveTimerRef.current = setTimeout(() => {
        writePosition(scrollTop);
        pendingScrollTop.current = null;
      }, debounceMs);
    },
    [storageKey, debounceMs],
  );

  const clearSavedPosition = useCallback(() => {
    memoryPositions.delete(stateKey);
    try {
      const hs = window.history.state ?? {};
      if (stateKey in hs) {
        const next = { ...hs };
        delete next[stateKey];
        window.history.replaceState(next, "");
      }
    } catch { /* ignore */ }
    try {
      sessionStorage.removeItem(storageKey);
    } catch { /* ignore */ }
  }, [storageKey, stateKey]);

  const saveNow = useCallback(() => {
    const el = scrollRef.current;
    if (!el || restoringRef.current) return;
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    writePosition(el.scrollTop);
    pendingScrollTop.current = null;
  }, [storageKey, scrollRef]);

  return { onScroll, clearSavedPosition, saveNow };
}
